import React, { useCallback, useState } from 'react'
import CustomModal from 'components/organisms/CustomModal'
import { GET_CLIENT_LEVEL_DASHBOARD } from 'share/queries/cases'
import { useMutation } from '@apollo/client'
import CasesListView from 'components/organisms/CasesListView'
import { ClientDashboardItem } from 'share/domain/case'
import ReactivateCaseModal from '../ReactivateCaseModal'
import {
  MutationCaseResponse,
  ReactivateCaseVariables,
  REACTIVATE_CASE
} from 'share/mutations/cases'
import { useDispatch } from 'react-redux'
import { incrementCaseCount } from 'reducers/authentication/authenticationSlice'

interface DeactivateCasesModalProps {
  matterList: ClientDashboardItem[] | undefined
  show: boolean
  onHide: () => void
}

const DeactivateCasesModal = (
  props: DeactivateCasesModalProps
): JSX.Element => {
  const [reactivateCase] =
    useMutation<MutationCaseResponse, ReactivateCaseVariables>(REACTIVATE_CASE)

  const [showList, setShowList] = useState(true)
  const [selectedCaseId, setSelectedCaseId] = useState(0)
  const dispatch = useDispatch()

  const handlePopupMenuClick = useCallback((caseId?: number) => {
    setSelectedCaseId(!caseId ? 0 : caseId)
    setShowList(false)
  }, [])

  const handleOnHideModal = useCallback(() => {
    setShowList(true)
    props.onHide()
  }, [])

  const handleReactivateClick = useCallback(async () => {
    const responseMutation = await reactivateCase({
      variables: { id: selectedCaseId as number },
      refetchQueries: [
        { query: GET_CLIENT_LEVEL_DASHBOARD, variables: { active: false } }
      ]
    })

    if (!responseMutation)
      console.log(
        `There was an error trying to reactivate case: ${selectedCaseId}`
      )

    dispatch(incrementCaseCount)
    setShowList(true)
  }, [selectedCaseId])

  const renderListView = () => {
    return (
      <CasesListView
        cases={props.matterList}
        showPopupMenu={true}
        popupMenuActions={[
          { text: 'Reactivate', onClick: handlePopupMenuClick }
        ]}
      />
    )
  }

  const renderReactivateView = () => {
    const selectedCase: ClientDashboardItem = props.matterList?.find(
      (item: ClientDashboardItem) => item.id === selectedCaseId
    ) as ClientDashboardItem
    return <ReactivateCaseModal case={selectedCase} />
  }

  return (
    <CustomModal
      showClose
      show={props.show}
      showFooter={!showList}
      onHide={handleOnHideModal}
      title={showList ? 'Deactivated Cases' : 'Reactivate Case'}
      footerButtons={[
        {
          text: 'Reactivate Case',
          onClick: handleReactivateClick,
          variant: 'secondary',
          testid: 'modal-reactivate-case-button'
        },
        {
          text: 'Cancel',
          onClick: () => setShowList(true),
          variant: 'secondary',
          testid: 'modal-reactivate-case-cancel-button'
        }
      ]}
    >
      {showList ? renderListView() : renderReactivateView()}
    </CustomModal>
  )
}

export default React.memo(DeactivateCasesModal)
